package com.elan.edubridge.ui.shell

import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.shared.domain.model.UserRole
import com.elan.edubridge.shared.domain.policy.MembershipPolicy
import com.elan.edubridge.ui.staff.AssignmentsPage
import com.elan.edubridge.ui.staff.BoardPage
import com.elan.edubridge.ui.staff.ContentReportsPage
import com.elan.edubridge.ui.staff.DashboardShell
import com.elan.edubridge.ui.staff.JoinApplicationsPage
import com.elan.edubridge.ui.staff.NoticesPage
import com.elan.edubridge.ui.staff.QrKioskPage
import com.elan.edubridge.ui.staff.SettingsPage
import com.elan.edubridge.ui.staff.StatsPage
import com.elan.edubridge.ui.staff.StudentsPage
import com.elan.edubridge.ui.staff.TodayDashboardPage
import com.elan.edubridge.ui.platform.PlatformApplicationsPage
import com.elan.edubridge.ui.platform.PlatformDashboardPage

/**
 * `/app` 허브 리다이렉트(가이드 §3.3) + 스태프 섹션 라우팅 분기.
 *
 * 스태프 콘솔 셸(사이드바·topbar)은 [com.elan.edubridge.ui.staff.DashboardShell]이 맡고, 이 파일은
 * ① `/app` 리다이렉트 판정 ② `section`별로 올바른 `ui.staff.*` 페이지를 호출하는 디스패치만 한다.
 * 회원 뷰(`/app/me` 이하)는 lane D의 `MemberRoutes`가 `Main.kt`에서 별도로 등록한다 —
 * 이 객체는 더 이상 회원 셸을 그리지 않는다(리뷰 후속: 옛 `renderMemberHome`/`memberShellHtml`
 * 플레이스홀더는 `MemberRoutes` 통합 이후 죽은 코드가 되어 삭제했다).
 */
object AppHub {
    /**
     * §3.3 `/app` 허브 판정. 순서: 미인증 → 보호자 동의 대기 → 전역 SUPER_ADMIN →
     * 스태프 멤버십(EDITOR/ADMIN) → 그 외 전부 회원 홈(§4.2 "학원 없음" 빈 상태 착지 포함).
     */
    fun resolveRedirect(session: SessionState): String {
        if (!session.isAuthenticated) return "/login?redirect=/app"
        if (session.requiresGuardianConsent) return "/app/guardian-consent"
        if (UserRole.SUPER_ADMIN in (session.account?.globalRoles ?: emptySet())) {
            return "/app/admin"
        }

        val membership = session.activeMembership
        if (membership != null && MembershipPolicy.isStaff(session)) {
            return "/app/${membership.academyId}/today"
        }
        return "/app/me"
    }

    /**
     * `/app/{academyId}/{section}`. VIEWER가 URL로 직접 접근하면 안전망으로 `/app/me`로 되돌린다(§3.3).
     * 각 섹션의 실제 화면은 `ui.staff.*` 페이지 객체가 그린다 — 여기는 라우팅 표 하나뿐이다.
     */
    fun renderStaffConsole(session: SessionState, academyId: String, section: String) {
        if (!MembershipPolicy.isStaff(session)) {
            Router.replace("/app/me")
            return
        }
        when (section) {
            "today" -> TodayDashboardPage.open(session, academyId)
            "students" -> StudentsPage.open(session, academyId)
            "attendance-qr" -> QrKioskPage.openManagement(session, academyId)
            // 제거된 출결 원장 섹션의 옛 북마크. 기록을 보러 온 사람이므로 대시보드가 아니라
            // 기간 출결 원장이 있는 통계로 보낸다(W10 — 이전에는 today로 보내 "전체 기록" 링크가
            // 제자리 순환하는 죽은 링크였다).
            "attendance" -> Router.replace("/app/$academyId/stats")
            "notices" -> NoticesPage.open(session, academyId)
            "board" -> BoardPage.open(session, academyId)
            "assignments" -> AssignmentsPage.open(session, academyId)
            "stats" -> StatsPage.open(session, academyId)
            "reports" -> ContentReportsPage.open(session, academyId)
            "join-applications" -> JoinApplicationsPage.open(session, academyId)
            "settings" -> SettingsPage.open(session, academyId)
            else -> DashboardShell.render(
                session,
                academyId,
                section,
                """<div class="eb-empty-state">${icon("icon-attendance", "xl")}<h2>출결 원장 준비 중이에요</h2><p>이 화면은 다음 단계에서 제공됩니다.</p></div>""",
            )
        }
    }

    /** `/app/{academyId}/qr/kiosk` — 사이드바 없는 별도 풀스크린 레이아웃(가이드 §3.5). */
    fun renderKiosk(session: SessionState, academyId: String) {
        if (!MembershipPolicy.isStaff(session)) {
            Router.replace("/app/me")
            return
        }
        QrKioskPage.openKiosk(session, academyId)
    }

    /** `/app/admin`. SUPER_ADMIN 전용 플랫폼 대시보드다. */
    fun renderAdminDashboard(session: SessionState) {
        if (UserRole.SUPER_ADMIN !in (session.account?.globalRoles ?: emptySet())) {
            Router.replace("/app")
            return
        }
        PlatformDashboardPage.open(session)
    }

    /** `/app/admin/applications`. SUPER_ADMIN 전용 승인 요청 목록이다. */
    fun renderAdminApplications(session: SessionState) {
        if (UserRole.SUPER_ADMIN !in (session.account?.globalRoles ?: emptySet())) {
            Router.replace("/app")
            return
        }
        PlatformApplicationsPage.open(session)
    }
}
